import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../UI/V3/Button/Button';
import SearchBar from '../../../UI/V3/SearchBar/SearchBar';
import styles from './UsersContent.module.scss';
import AddUserModal from '../Add/AddUserModal';
import {
    fetchOrganizationManagersThunk,
    deleteOrganizationManagerThunk
} from 'actions/organizationManager/OrganizationManagerActions';
import { fetchOrganizationsThunk } from 'actions/organization/OrganizationActions';
import { RootState } from 'store';
import { OrganizationManagerUser } from 'model/organizationManager';
import { ROLE_LABELS } from 'model/user';
import Spinner from 'components/UI/V3/Spinner';
import { ConfirmDialog } from 'components/UI/ConfirmDialog/ConfirmDialog';
import { showSuccessThunk } from '@spike/notifications-action';
import { RootAction } from 'actions/ActionTypes';

/**
 * Format date string to "MMMM dd, yyyy" format
 * @param dateString - ISO date string or any parseable date string
 * @returns Formatted date string (e.g., "January 15, 2024")
 */
const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Never';

    try {
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) return 'Invalid Date';

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return 'Invalid Date';
    }
};

export const UsersContent: React.FC = () => {
    // Redux
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const { organizations } = useSelector((state: RootState) => state.organizations);
    const { organizationManagers, loading } = useSelector((state: RootState) => state.organizationManagers);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<OrganizationManagerUser | null>(null);
    const [searchText, setSearchText] = useState('');
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<OrganizationManagerUser | null>(null);

    // Fetch organizations on component mount
    useEffect(() => {
        if (organizations.length === 0) {
            dispatch(fetchOrganizationsThunk());
        }
    }, [dispatch]);

    // Fetch organization managers when organizations are loaded
    useEffect(() => {
        const fetchUsers = async () => {
            // Find the parent organization (the one with parent_organization_id: null)
            const parentOrganization = organizations.find(org => !org.parentOrganizationId);
            const organizationId = parentOrganization?.id;

            if (organizationId) {
                dispatch(fetchOrganizationManagersThunk(organizationId));
            }
        };

        if (organizations.length > 0 && organizationManagers.length === 0) {
            fetchUsers();
        }
    }, [dispatch, organizations]);

    // Filter users based on search text
    const filteredUsers = organizationManagers.filter(user => {
        if (!searchText) return true;

        const searchLower = searchText.toLowerCase();
        return (
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
        );
    });

    const handleAddUser = () => {
        setEditingUser(null);
        setIsAddUserModalOpen(true);
    };

    const handleEditUser = (user: OrganizationManagerUser) => {
        setEditingUser(user);
        setIsAddUserModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        try {
            // Revokes the membership only - the account and any membership in
            // another organization survive
            await dispatch(deleteOrganizationManagerThunk(userToDelete.organizationManagerId));

            // Close dialog and reset state
            setIsDeleteDialogOpen(false);
            setUserToDelete(null);

            // Refetch organization managers to update the list
            const parentOrganization = organizations.find(org => !org.parentOrganizationId);
            if (parentOrganization) {
                await dispatch(fetchOrganizationManagersThunk(parentOrganization.id));
            }
            // Show success notification with appropriate message
            dispatch(showSuccessThunk(`User removed from the organization`) as any);
        } catch (error) {
            console.error('Error removing user from organization:', error);
            // Dialog will remain open on error, allowing the user to retry or cancel
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleUserSaveSuccess = () => {
        // Refetch organization managers to get the updated list
        const parentOrganization = organizations.find(org => !org.parentOrganizationId);
        if (parentOrganization) {
            dispatch(fetchOrganizationManagersThunk(parentOrganization.id));
        }
    };

    const handleCloseUserModal = () => {
        setIsAddUserModalOpen(false);
        setEditingUser(null);
    };

    const handleSearchChange = (text: string) => {
        setSearchText(text);
    };

    const renderUsersList = () => (
        <div className={styles.usersList}>
            {filteredUsers.map(user => (
                <div
                    key={user.id}
                    className={styles.userCard}
                >
                    <div className={styles.userHeader}>
                        <div className={styles.userInfo}>
                            <h3 className={styles.userName}>
                                {user.firstName} {user.lastName} · {ROLE_LABELS[user.accessLevelId] || 'Unknown'}
                            </h3>
                            {} <p className={styles.userEmail}>{user.email}</p>
                        </div>
                        <div className={styles.userActions}>
                            <button
                                className={styles.actionButton}
                                onClick={() => {
                                    setIsDeleteDialogOpen(true);
                                    setUserToDelete(user);
                                }}
                                title="Remove from organization"
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            <button
                                className={styles.actionButton}
                                onClick={() => handleEditUser(user)}
                                title="Edit"
                            >
                                <FontAwesomeIcon icon={faPen} />
                            </button>
                        </div>
                    </div>

                    <div className={styles.userMeta}>
                        <p className={styles.userDate}>
                            <span>Date added:</span> <strong>{formatDate(user.createdAt)}</strong> ·{' '}
                            <span>Last active:</span> <strong>{formatDate(user.updatedAt)}</strong>
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className={styles.usersContent}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <div>
                        <h1 className={styles.title}>Users</h1>
                    </div>
                    <div>
                        <Button
                            label="Add user"
                            size="large"
                            variant="outlined"
                            onClick={handleAddUser}
                        />
                    </div>
                </div>

                {/* Search Bar */}
                <div className={styles.searchContainer}>
                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by name"
                        className={styles.searchBar}
                    />
                </div>
            </div>

            <div className={styles.content}>
                {loading && filteredUsers.length === 0 ? (
                    <div className={styles.noResults}>
                        <p>No users found matching &quot;{searchText}&quot;</p>
                    </div>
                ) : loading ? (
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingContainer}>
                            <Spinner size={32} />
                        </div>
                    </div>
                ) : (
                    renderUsersList()
                )}
            </div>

            {/* Add User Modal */}
            <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={handleCloseUserModal}
                onSuccess={handleUserSaveSuccess}
                editingUser={editingUser}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={isDeleteDialogOpen}
                title="Remove User"
                question={
                    userToDelete
                        ? `Are you sure you want to remove ${userToDelete.firstName} ${userToDelete.lastName} from this organization?`
                        : 'Are you sure you want to remove this user from this organization?'
                }
                subquestion="Their account will remain active, along with access to any other organization they belong to."
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Remove"
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                processing={loading}
                className={styles.deleteConfirmDialog}
            />
        </div>
    );
};

export default UsersContent;
